import { useEffect } from "react";

const SITE_NAME = "Shonei blogs";
const DEFAULT_DESCRIPTION =
  "Software engineering notes and writing from Shonei — a developer navigating the world.";

export type PageMeta = {
  title?: string;
  description?: string;
  path?: string;
  image?: string | null;
  type?: "website" | "article";
  noindex?: boolean;
  /** Serialized as JSON for effect deps — pass a stable object or memoize. */
  jsonLd?: Record<string, unknown> | null;
};

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertJsonLd(data: Record<string, unknown> | null) {
  const id = "page-json-ld";
  const existing = document.getElementById(id);
  if (!data) {
    existing?.remove();
    return;
  }
  const el =
    (existing as HTMLScriptElement | null) ??
    Object.assign(document.createElement("script"), {
      id,
      type: "application/ld+json",
    });
  el.textContent = JSON.stringify(data);
  if (!existing) document.head.appendChild(el);
}

export function usePageMeta({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  image,
  type = "website",
  noindex = false,
  jsonLd = null,
}: PageMeta) {
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const previousTitle = document.title;
    const pageTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const origin = window.location.origin;
    const url = `${origin}${path ?? window.location.pathname}`;
    const imageUrl = image
      ? image.startsWith("http")
        ? image
        : `${origin}${image.startsWith("/") ? "" : "/"}${image}`
      : undefined;

    document.title = pageTitle;

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:title", pageTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    if (imageUrl) upsertMeta("property", "og:image", imageUrl);

    upsertMeta("name", "twitter:card", imageUrl ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", pageTitle);
    upsertMeta("name", "twitter:description", description);
    if (imageUrl) upsertMeta("name", "twitter:image", imageUrl);

    upsertLink("canonical", url);
    upsertJsonLd(jsonLd);

    return () => {
      document.title = previousTitle;
      upsertJsonLd(null);
    };
  }, [title, description, path, image, type, noindex, jsonLd, jsonLdKey]);
}

export { SITE_NAME, DEFAULT_DESCRIPTION };
